// Reporter-facing copy for the OnCatch widget.
//
// Every locale exports a `WidgetMessages` object matching this shape.
// New strings get added here first; TS will fail the build until every
// locale dictionary supplies the new key.
//
// Function-shaped fields are used for messages that interpolate a value
// (workspace name, retention days, recording duration). Keeping them as
// functions instead of `${}`-formatted templates means each locale can
// re-order arguments naturally — German consent copy can put the
// workspace name in a different sentence position than English.

export interface WidgetMessages {
  // ── Form panel ────────────────────────────────────────────────────────
  panelTitle: string;
  panelClose: string;
  fieldTitleLabel: string;
  fieldTitlePlaceholder: string;
  fieldSummaryLabel: string;
  fieldSummaryPlaceholder: string;
  fieldNameLabel: string;
  fieldEmailLabel: string;
  /**
   * Opt-in checkbox below the name/email fields. When ticked, the widget
   * remembers the reporter's contact details in host-origin localStorage and
   * prefills them on the next open. Clearing it wipes the stored copy.
   */
  fieldSaveContactLabel: string;
  /**
   * Shown beside the save-contact checkbox when the browser blocks storage
   * (Safari private mode, partitioned storage). Without it the opt-in would
   * appear to work and simply never prefill.
   */
  fieldSaveContactUnavailable: string;
  /**
   * #648 (OC-5): opt-in checkbox rendered below the email field (only when
   * the workspace enabled reporter fix notifications, and only visible while
   * the email field is non-empty). Ticking it asks to be emailed when the
   * fix for this report ships.
   */
  fieldNotifyOnFixLabel: string;
  buttonSend: string;
  buttonSending: string;
  /**
   * #533 — submit-button label while the widget waits for attachments
   * that were still compressing / reserving / uploading at click time.
   * Distinct from buttonSending so the reporter knows the delay is their
   * screenshot finishing, not the report failing to send.
   */
  buttonWaitingForUploads: string;
  /**
   * Shown when the title is empty on submit. Details is optional, so the
   * title is the only field that can block a submission.
   */
  errorTitleRequired: string;
  successHeading: string;
  successCopy: (referenceCode: string) => string;
  /**
   * #256 — partial-success notice rendered ABOVE the reference code when the
   * server's reconcile loop dropped some attachments (e.g. MIME-mismatch,
   * Storage object missing, attachment-row insert failed). Failed/total are
   * counts of submitted-vs-dropped reservations.
   */
  successPartialNotice: (failed: number, total: number) => string;
  successAnotherButton: string;
  poweredBy: string;

  // ── Recording consent modal ──────────────────────────────────────────
  recordingConsentTitle: string;
  /** First paragraph; "this site" fallback when workspaceName is empty. */
  recordingConsentLead: (workspaceName: string) => string;
  recordingConsentBulletAdminsOnly: string;
  recordingConsentBulletMicAudio: string;
  recordingConsentBulletMaxLength: string;
  recordingConsentBulletRetention: (retentionDays: number) => string;
  recordingConsentBulletDeletion: string;
  recordingConsentCancelBtn: string;
  recordingConsentAcceptBtn: string;

  // ── Recording mode chooser (#770) ───────────────────────────────────
  recordingChoiceTitle: string;
  recordingChoiceLead: string;
  recordingChoiceScreen: string;
  recordingChoiceScreenDetail: string;
  recordingChoiceCamera: string;
  recordingChoiceCameraDetail: string;
  recordingChoiceAudio: string;
  recordingChoiceAudioDetail: string;
  recordingChoiceUnavailable: string;
  recordingAudioConsentTitle: string;
  recordingAudioConsentLead: (workspaceName: string) => string;
  recordingAudioConsentTip: string;

  // ── Recording overlay ────────────────────────────────────────────────
  /** "Recording 0:00" idle state shown before timer ticks. */
  recordingTimerIdle: string;
  /** "Recording m:ss" running state. */
  recordingTimer: (minutes: number, seconds: number) => string;
  recordingStopBtn: string;
  recordingStartedAnnouncement: string;
  /** "Recording 25 seconds" cadence announcement for screen readers. */
  recordingProgressAnnouncement: (elapsedSec: number) => string;

  // ── Capture (region select) overlay ──────────────────────────────────
  captureRegionHelp: string;
  captureLabelCapturing: string;
  captureLabelPreparing: string;
  /** #331 — overlay shown while the selected region is being rasterized. */
  captureRegionCapturing: string;

  // ── Safari unsupported banner (#163 PR C) ────────────────────────────
  safariUnsupportedTitle: string;
  safariUnsupportedBody: string;

  // ── Attachments section ──────────────────────────────────────────────
  attachmentsLabel: string;
  captureWholePageBtn: string;
  captureSelectRegionBtn: string;
  /** #215 — pin mode: click an element on the host page to attach it. */
  captureSelectElementBtn: string;
  captureRecordBtn: string;
  /** #297 — surfaced when the user denies the camera prompt mid-recording. */
  cameraDeniedFallback: string;
  /** #297 — consent-modal bullet, shown while the camera opt-in is ticked. */
  recordingConsentBulletCamera: string;
  /**
   * Consent-modal opt-in that upgrades a screen recording to screen + camera.
   * Replaces the former standalone "Record + camera" capture button, so the
   * camera decision is made inside the consent step rather than before it.
   */
  recordingConsentIncludeCameraLabel: string;
  recordingUnsupportedInline: string;

  // ── Upload dropzone (#605) ───────────────────────────────────────────
  /** Button inside the dropzone that opens the native file picker. */
  uploadDropzoneBtn: string;
  /** Helper line under the button describing accepted file types. */
  uploadDropzoneHint: (remaining: number) => string;
  /** Replaces the hint at cap; also the inline error for a drop/paste at cap. */
  uploadDropzoneCapReached: string;
  uploadErrorUnsupportedType: string;
  uploadErrorFileTooLarge: string;
  /** Defensive fallback for an unexpected upload-pipeline rejection. */
  uploadErrorGeneric: string;
  /** Multi-select overflow: first N added, `skipped` rejected at the cap. */
  uploadErrorTooMany: (skipped: number) => string;

  // ── Storage cap (#136) ───────────────────────────────────────────────
  storageFullNotice: string;
  storageFullDetailedNotice: string;

  // ── Challenge (Turnstile) ────────────────────────────────────────────
  challengeIntro: string;
  challengeFailed: string;

  // ── Annotation toolbar (#210) ────────────────────────────────────────
  annotateTitle: string;
  toolArrow: string;
  toolBox: string;
  toolPen: string;
  toolText: string;
  colorRed: string;
  colorYellow: string;
  colorGreen: string;
  colorBlack: string;
  buttonUndo: string;
  buttonClear: string;
  buttonSkip: string;
  buttonSave: string;
  textInputPlaceholder: string;
  saveError: string;
  activeIndicator: string;
}

export type SupportedLocale = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pt' | 'nl';

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = [
  'en',
  'es',
  'fr',
  'de',
  'it',
  'pt',
  'nl',
] as const;
